import { useRouter } from 'next/router';

import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';
import { getFullUserName } from '@sravni/ugc/lib/utils/getFullUserName';

import { BOOLEAN_STRING_TO_CUSTOMER_TYPE } from '../../../constants/reviews';
import { useProductsTags, useServerFilters } from '../../../store/hooks/filters';
import { useCurrentRegion } from '../../../store/hooks/locations';
import { useOrganizations } from '../../../store/hooks/organizations';
import { useReviewItem } from '../../../store/hooks/reviews';
import { useUser } from '../../../store/hooks/users';
import { TCreateReviewPageQuery } from '../../../types/router';
import { getDate } from '../../../utils/dateTime';
import { dateFormat } from '../../../utils/yup';
import { REVIEW_FIELDS } from '../constants/common';
import { getServiceFromAllData } from '../helpers/helpers';
import { TFormInputs } from '../types';

type TGetInitDataProps = {
  isEdit: boolean;
};

export const useGetInitData = (props: TGetInitDataProps): TFormInputs => {
  const { isEdit } = props;

  const { query } = useRouter() as TRouter<TCreateReviewPageQuery>;
  const user = useUser();
  const { item: review } = useReviewItem();

  const location = useCurrentRegion();
  const organizations = useOrganizations();

  const productTags = useProductsTags();
  const serverFilters = useServerFilters();

  let organizationHelper = getOrganizationHelpers(query.type);

  let organizationId =
    query?.reviewObjectId ?? query?.organization ?? organizations.details.infoAliasMap[query?.organization] ?? null;
  let locationGarId = Number(query?.regionId ?? location.garId);
  let birthdate = query.birthdate ?? user?.birthdate;
  let phone = query.phone ?? user?.phone_number;

  let rating = query?.rate;
  let fio =
    query.name ??
    `${user?.family_name || ''} ${user?.given_name || ''}`.trim() ??
    getFullUserName({
      firstName: user?.first_name,
      lastName: user?.last_name,
      type: 'long',
    });
  let productId = query.specificProduct ?? null;
  let attachments = [];
  let isLegal = null;

  if (isEdit && review) {
    organizationHelper = getOrganizationHelpers(review.reviewObjectType, productTags);
    birthdate = review.reviewUserData?.userBirthDateForAppeal || user?.birthdate;
    phone = review.reviewUserData?.userPhoneForAppeal || user?.phone_number;
    rating = review.rating === 0 ? review.ratingBase : review.rating;
    locationGarId = review.locationGarId;
    organizationId = review?.reviewObjectId;
    fio = review?.reviewUserData?.userNameForAppeal;
    // productName не восстанавливаем, тк хватает productId
    productId = review.specificProductId ?? null;
    attachments = review.attachments?.filter((attachment) => attachment) ?? [];
    isLegal = BOOLEAN_STRING_TO_CUSTOMER_TYPE[`${review.isLegal}`];
  }

  const organizationType = organizationHelper.typeForService ?? null;

  // получаем услугу из всех данных, что есть в урле
  const service =
    getServiceFromAllData({
      review,
      serverFilters,
      organizationType,
      query,
      productTags,
    }) ?? null;

  return {
    [REVIEW_FIELDS.ORGANIZATION_TYPE]: organizationType,
    [REVIEW_FIELDS.SERVICE]: service,
    [REVIEW_FIELDS.IS_LEGAL]: isLegal,
    [REVIEW_FIELDS.ORGANIZATION_ID]: organizationId,
    [REVIEW_FIELDS.LOCATION_GAR_ID]: locationGarId,
    [REVIEW_FIELDS.TITLE]: isEdit ? review?.title : '',
    [REVIEW_FIELDS.TEXT]: isEdit ? review?.text : '',
    [REVIEW_FIELDS.USER_NAME]: fio,
    [REVIEW_FIELDS.BIRTHDAY]: birthdate ? getDate(birthdate, dateFormat) : '',
    [REVIEW_FIELDS.USER_PHONE]: phone ?? '',
    [REVIEW_FIELDS.MESSAGE_FOR_COMPANY]: isEdit ? review?.messageForCompany : '',
    [REVIEW_FIELDS.RATING]: rating,
    [REVIEW_FIELDS.PRODUCT_ID]: productId,
    [REVIEW_FIELDS.ATTACHMENTS]: attachments,
    [REVIEW_FIELDS.AGREEMENT]: true,
    [REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT]: false,
    ...(isEdit && { [REVIEW_FIELDS.ID]: review.id }),
  };
};
